'use client';

import { ServerTimeContext } from '@contexts/ServerTimeContext';
import { Duration, intervalToDuration } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import { getAdjustedClientTime } from 'utils';

const ONE_SECOND_IN_MILLISECONDS = 1000;

/**
 * True while the duration still has time on the clock. Card countdowns must
 * never display a zero or negative value (CPS-7269): the sale-state flip that
 * unmounts them (useIsActiveSale) can race the 1s tick around a boundary,
 * where intervalToDuration yields an all-zero or negative duration.
 */
export const hasCountdownTimeRemaining = (duration: Duration) =>
  Object.values(duration).some(
    (value) => typeof value === 'number' && value > 0
  );

/**
 * Counts down every second and returns the duration of time until the targetDate.
 * @param targetDate
 * @returns Duration of time until the targetDate. See date-fns Duration for information on usage and formatting.
 */
export const useCountdown = (
  targetDate?: Date,
  countdownIncrement: number = ONE_SECOND_IN_MILLISECONDS
) => {
  const { serverTimeOffsetInMs } = useContext(ServerTimeContext);
  const [countDownDuration, setCountDownDuration] = useState<Duration | null>(
    targetDate
      ? intervalToDuration({
          start: getAdjustedClientTime(new Date(), serverTimeOffsetInMs),
          end: targetDate,
        })
      : null
  );

  useEffect(() => {
    if (!targetDate) {
      return;
    }

    // Recompute immediately so a target switch (e.g. a sale flipping from
    // upcoming to active swaps starts → ends) doesn't display the previous
    // target's stale duration until the next tick.
    setCountDownDuration(
      intervalToDuration({
        start: getAdjustedClientTime(new Date(), serverTimeOffsetInMs),
        end: targetDate,
      })
    );

    const interval = setInterval(() => {
      setCountDownDuration(
        intervalToDuration({
          start: getAdjustedClientTime(new Date(), serverTimeOffsetInMs),
          end: targetDate,
        })
      );
    }, countdownIncrement);

    return () => {
      clearInterval(interval);
    };
  }, [targetDate, countdownIncrement, serverTimeOffsetInMs]);

  return countDownDuration;
};
